import { useStaticData } from "context/staticData";
import { useLocation } from "react-router-dom";
import { BaseApi } from "shared/api/BaseApi";
import { useGetApiData, usePostApiData } from "shared/datalayer";
import { useCopies } from "shared/hooks/useCopies";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import ApiAdminReportDownloadRequest from "shared/types/ApiAdminReportDownloadRequest";
import { ApiEventParticipant } from "shared/types/ApiEventParticipant";
import ApiContactContext from "shared/types/contact/ApiContactContext";
import { ApiContactContextListResponse } from "shared/types/contact/ApiContactContextListResponse";
import { ApiContactContextRequest_Create } from "shared/types/contact/ApiContactContextRequest";
import { ApiContextEnableRequest } from "shared/types/contact/ApiContextEnableRequest";
import { successToast } from "shared/utils/toastNotification";

export function useCreateContactContext(baseApiUtil: BaseApi) {
  const { navigate } = useNavigateWrapper();
  const copies = useCopies();
  const staticData = useStaticData();
  const location = useLocation();

  return usePostApiData<ApiContactContext, ApiContactContextRequest_Create>(baseApiUtil.createContactContext(), {
    onSuccess: data => {
      const contactMode = staticData.contactMode[data.contactMode];
      successToast(
        copies.get("contact.context.created.successfully", {
          0: contactMode
        })
      );
      navigate(location.pathname.replace("create", data.id), { replace: true });
    }
  });
}

export function useUpdateContactContext(baseApiUtil: BaseApi) {
  const copies = useCopies();
  const staticData = useStaticData();
  return usePostApiData<ApiContactContext, ApiContactContextRequest_Create>(baseApiUtil.updateContactContext(), {
    onSuccess: data => {
      const contactMode = staticData.contactMode[data.contactMode];
      successToast(
        copies.get("contact.context.updated.successfully", {
          0: contactMode
        })
      );
    }
  });
}
export function useUpdateContactContextStatus(baseApiUtil: BaseApi) {
  const copies = useCopies();
  return usePostApiData<ApiContactContext, ApiContextEnableRequest>(baseApiUtil.updateContextStatus(), {
    onSuccess: data => {
      successToast(copies.get("contact.context.location.status.update.success.msg"));
    }
  });
}

export function useGetContactContext({
  baseApiUtil,
  requesterId,
  contextId
}: {
  baseApiUtil: BaseApi;
  requesterId?: string;
  contextId: string;
}) {
  return useGetApiData<ApiContactContextListResponse>(
    baseApiUtil.getContactContext(),
    {
      params: {
        requesterId: requesterId
      },
      pathParams: {
        contactContextId: contextId
      }
    },
    {
      enabled: !!contextId
    }
  );
}

export function useGetEventParticipantsList({
  baseApiUtil,
  requesterId
}: {
  baseApiUtil: BaseApi;
  requesterId?: string;
}) {
  return useGetApiData<Array<ApiEventParticipant>>(
    baseApiUtil.getParticipantsToAdd(),
    {
      params: {
        eventParticipantId: requesterId
      }
    },
    {
      enabled: !!requesterId
    }
  );
}

export function useDownloadAttendeeListInContactContext(baseApiUtil: BaseApi, contextId: string) {
  const copies = useCopies();
  return usePostApiData<any, ApiAdminReportDownloadRequest>(baseApiUtil.downloadAttendeeList(), {
    onSuccess: () => {
      successToast(copies.get("contacts.report.download.success"));
    }
  });
}
